import { Link } from 'react-router-dom';
import HERO_IMAGES from '../heroImages';
import SEO from '../components/SEO/SEO';
import {
  Award, Clock, MessageSquare, Headphones, ArrowRight,
  Target, ShieldCheck, TrendingUp, Handshake,
  Search, PenTool, Code2, ShieldAlert, Rocket, Gauge,
  Compass, Shield, Rocket as RocketIcon,
} from 'lucide-react';
import './AboutPage.css';

const stats = [
  { value: '20+', label: 'Active Clients' },
  { value: '100+', label: 'Projects Delivered' },
  { value: '24/7', label: 'Support Available' },
  { value: '100%', label: 'Client Satisfaction' },
];

const badges = [
  { icon: <Compass size={16} />, label: 'Strategy First' },
  { icon: <Shield size={16} />, label: 'Security, Not Bolted On' },
  { icon: <RocketIcon size={16} />, label: 'Built to Scale' },
  { icon: <Handshake size={16} />, label: 'Long-Term Partner' },
];

const journey = [
  { year: '2023', title: 'The Beginning', desc: 'A small team with a big vision, founded to build software that actually holds up.' },
  { year: '2024', title: 'First Enterprise Wins', desc: 'Delivered our first enterprise-grade platforms and grew a loyal early client base.' },
  { year: '2025', title: 'Cybersecurity Focus', desc: 'Formalised our security practice — VAPT, audits, and secure-by-design development.' },
  { year: '2026', title: 'Scaling Up', desc: 'Now serving 20+ active clients across India, with automation and marketing added to the stack.' },
];

const dna = [
  { icon: <Target size={22} />, title: 'Build With Purpose', desc: 'Every project starts by solving a real business problem — not shipping features for their own sake.', color: '#0A4FFF' },
  { icon: <ShieldCheck size={22} />, title: 'Security by Design', desc: "Security isn't an afterthought. We build it into the first line of code, every time.", color: '#7C3AED' },
  { icon: <TrendingUp size={22} />, title: 'Scalable Architecture', desc: 'We build for where your business is going, not just where it is today.', color: '#059669' },
  { icon: <Handshake size={22} />, title: 'Long-Term Partnership', desc: "Your success is ours. We stay involved well past launch day.", color: '#D97706' },
];

const process = [
  { icon: <Search size={20} />, title: 'Discover', desc: 'We understand your business, audience, and goals.' },
  { icon: <PenTool size={20} />, title: 'Design', desc: 'We create intuitive experiences people actually enjoy using.' },
  { icon: <Code2 size={20} />, title: 'Develop', desc: 'We build robust, scalable, high-performance solutions.' },
  { icon: <ShieldAlert size={20} />, title: 'Secure', desc: 'We test and audit every layer before it ships.' },
  { icon: <Rocket size={20} />, title: 'Deploy', desc: 'We launch with best practices and zero-downtime releases.' },
  { icon: <Gauge size={20} />, title: 'Optimise', desc: 'We monitor, measure, and keep improving after launch.' },
];

const values = [
  {
    icon: <Award size={22} />,
    title: 'Quality First',
    desc: 'Clean, scalable, and maintainable code on every project. We never cut corners.',
  },
  {
    icon: <Clock size={22} />,
    title: 'On-Time Delivery',
    desc: 'We set realistic timelines, track them rigorously, and deliver — always.',
  },
  {
    icon: <MessageSquare size={22} />,
    title: 'Clear Communication',
    desc: 'Regular updates, fast responses, and full transparency throughout.',
  },
  {
    icon: <Headphones size={22} />,
    title: 'Post-Launch Support',
    desc: "We don't disappear after delivery. We're your long-term technology partner.",
  },
];

export default function AboutPage() {
  return (
    <main className="about-page">
      <SEO
        title="About Us — Engineering Digital Products That Last"
        description="ExploitEye is a full-stack digital agency helping businesses bring ideas to life with clean code, thoughtful design, and reliable delivery. 20+ active clients, 100+ projects delivered."
        path="/about"
        keywords="about ExploitEye, digital agency India, software development company Chennai"
      />
      <section className="page-hero" style={{ backgroundImage: `url(${HERO_IMAGES.about})` }}>
        <div className="container" style={{ position: 'relative', zIndex: 1 }}>
          <span className="section-label">About Us</span>
          <h1>
            We're Building
            <br />
            <span className="gradient-text">Digital Excellence.</span>
          </h1>
          <p>
            ExploitEye is a digital agency combining technical expertise with creative thinking
            to deliver solutions that work — and last.
          </p>
          <div className="about-badges">
            {badges.map((b, i) => (
              <span key={i} className="about-badge">
                {b.icon} {b.label}
              </span>
            ))}
          </div>
        </div>
      </section>

      <div className="about-hero__stats">
        <div className="container">
          <div className="stats-track">
            {stats.map((s, i) => (
              <div key={i} className="stat-item">
                <strong>{s.value}</strong>
                <span>{s.label}</span>
              </div>
            ))}
          </div>
        </div>
      </div>

      <section className="about-why-journey">
        <div className="container">
          <div className="why-journey__grid">
            <div className="why-journey__why">
              <span className="section-label">Why We Exist</span>
              <h2 className="section-heading">
                Businesses Deserve Software
                <br />
                That Performs, Scales & Stays Secure.
              </h2>
              <p>
                ExploitEye was founded on a simple belief — every business, no matter its size,
                deserves enterprise-grade technology and security, not an afterthought bolted on
                once something goes wrong.
              </p>
              <p>
                We combine engineering discipline with cybersecurity expertise to turn ideas into
                digital products that actually hold up in the real world.
              </p>
            </div>
            <div className="why-journey__timeline">
              <span className="section-label">Our Journey</span>
              <div className="journey-track">
                {journey.map((j, i) => (
                  <div key={i} className="journey-item">
                    <div className="journey-item__dot">{j.year}</div>
                    <strong>{j.title}</strong>
                    <span>{j.desc}</span>
                  </div>
                ))}
              </div>
            </div>
          </div>
        </div>
      </section>

      <section className="about-dna">
        <div className="container">
          <div className="section-center">
            <span className="section-label">Our DNA</span>
            <h2 className="section-heading" style={{ textAlign: 'center' }}>What Drives Every Project</h2>
          </div>
          <div className="dna-grid">
            {dna.map((d, i) => (
              <div key={i} className="dna-card" style={{ '--dna-color': d.color }}>
                <div className="dna-card__icon">{d.icon}</div>
                <h3>{d.title}</h3>
                <p>{d.desc}</p>
              </div>
            ))}
          </div>
        </div>
      </section>

      <section className="about-process">
        <div className="container">
          <div className="section-center">
            <span className="section-label">How We Work</span>
            <h2 className="section-heading" style={{ textAlign: 'center' }}>Our Six-Step Process</h2>
          </div>
          <div className="process-track">
            {process.map((p, i) => (
              <div key={i} className="process-step">
                <div className="process-step__icon">{p.icon}</div>
                <strong>{p.title}</strong>
                <span>{p.desc}</span>
                {i < process.length - 1 && <div className="process-step__connector" />}
              </div>
            ))}
          </div>
        </div>
      </section>

      <section className="about-stats-banner">
        <div className="container">
          <div className="stats-banner__track">
            {stats.map((s, i) => (
              <div key={i} className="stats-banner__item">
                <strong>{s.value}</strong>
                <span>{s.label}</span>
              </div>
            ))}
          </div>
        </div>
      </section>

      <section className="about-story">
        <div className="container">
          <div className="about-story__grid">
            <div className="about-story__text">
              <span className="section-label">Who We Are</span>
              <h2 className="section-heading">
                Engineering Digital
                <br />
                Products That Last.
              </h2>
              <p>
                ExploitEye is a full-stack digital agency helping businesses bring their ideas to
                life with clean code, thoughtful design, and reliable delivery.
              </p>
              <p>
                We specialise in web development, mobile applications, UI/UX design, automation,
                digital marketing, and enterprise cybersecurity — building products that scale
                with your business and stand the test of time.
              </p>
              <p>
                From early-stage startups to established enterprises, we partner with clients who
                value craftsmanship, communication, and long-term results over quick fixes.
              </p>
            </div>

            <div className="about-story__terminal">
              <div className="terminal__bar">
                <span className="terminal__dot terminal__dot--r" />
                <span className="terminal__dot terminal__dot--y" />
                <span className="terminal__dot terminal__dot--g" />
                <span className="terminal__title">about-exploiteye.js</span>
              </div>
              <div className="terminal__body">
                <pre className="terminal__code">
                  <span className="t-dim">// What we do</span>
                  {'\n\n'}
                  <span className="t-blue">const</span>{' '}
                  <span className="t-green">exploiteye</span> = {'{'}
                  {'\n'}
                  {'  '}type: <span className="t-str">"Digital Agency"</span>,{'\n'}
                  {'  '}services: [<span className="t-str">"Web"</span>,{' '}
                  <span className="t-str">"Mobile"</span>,{' '}
                  <span className="t-str">"UI/UX"</span>,{' '}
                  <span className="t-str">"Security"</span>],{'\n'}
                  {'  '}clients: <span className="t-str">"20+"</span>,{'\n'}
                  {'  '}projects: <span className="t-str">"100+"</span>,{'\n'}
                  {'  '}focus: <span className="t-str">"Quality &amp; Delivery"</span>,{'\n'}
                  {'  '}mission: <span className="t-str">"Build digital products{'\n'}           that actually work."</span>,{'\n'}
                  {'}'};
                </pre>
              </div>
            </div>
          </div>
        </div>
      </section>

      <section className="about-values">
        <div className="container">
          <div className="about-values__grid">
            <div className="about-values__left">
              <span className="section-label">Why ExploitEye</span>
              <h2 className="section-heading">
                Partners, Not
                <br />
                Just Vendors.
              </h2>
              <p
                style={{
                  color: 'var(--text-2)',
                  lineHeight: 1.75,
                  marginBottom: 32,
                  marginTop: 16,
                }}
              >
                We treat every project as our own product — obsessing over detail, communicating
                clearly, and staying accountable until delivery and beyond.
              </p>
              <Link
                to="/contact"
                className="btn-outline"
                style={{
                  color: 'var(--brand-primary)',
                  borderColor: 'var(--brand-primary)',
                }}
              >
                Work With Us <ArrowRight size={15} />
              </Link>
            </div>
            <div className="about-values__right">
              {values.map((v, i) => (
                <div key={i} className="value-pill">
                  <div className="value-pill__icon">{v.icon}</div>
                  <div>
                    <strong>{v.title}</strong>
                    <span>{v.desc}</span>
                  </div>
                </div>
              ))}
            </div>
          </div>
        </div>
      </section>

      <section className="cta-strip">
        <div className="container">
          <div className="cta-strip-inner">
            <div className="cta-strip-text">
              <h2>Let's Build Something Amazing.</h2>
              <p>Ready to transform your ideas into reality?</p>
            </div>
            <div className="cta-strip-actions">
              <Link to="/contact" className="btn-primary">
                Start a Conversation <ArrowRight size={16} />
              </Link>
              <Link to="/portfolio" className="btn-ghost">
                View Our Work
              </Link>
            </div>
          </div>
        </div>
      </section>
    </main>
  );
}